import { useCallback, useEffect, useRef, type RefObject } from 'react'

/* -----------------------------------------------------------------------------
   THE ESCAPE STACK — which surface a key press belongs to.

   Added with the first modal (REC-20), because the modal is what made the
   question real. Before it there was never more than one floating surface open
   at a time, so "am I the one being dismissed" had an obvious answer and
   `contains(document.activeElement)` was enough.

   A person picker inside a dialog breaks that: BOTH surfaces contain the focused
   input, so both claim Escape, and the one that answers first closes the whole
   form and discards a half-written message to a colleague.

   ORDERING CANNOT DECIDE IT, WHICH IS THE PART WORTH WRITING DOWN. React 18
   attaches its handlers to the root container, so a React onKeyDown always runs
   BEFORE any document listener — and among document listeners the earlier
   registration wins, which is the OUTER surface in every case. So every
   arrangement of listeners gets this backwards, and the only thing that knows
   the answer is which surface opened last.

   Hence a stack. The topmost open surface takes the key; everything below
   ignores it. `isTopmost` is read at press time, never captured in a closure,
   because the stack changes while a surface is open.

   ONE CAVEAT, STATED RATHER THAN HIDDEN: child effects run before parent
   effects, so two surfaces MOUNTING open in the same commit push innermost
   first and the outer one would win. Nothing does that today — every surface
   here starts closed and opens on an interaction — and a menu that renders
   already-open would need to push on mount order instead.
   -------------------------------------------------------------------------- */
const openSurfaces: object[] = []

export function useSurfaceLayer(open: boolean): () => boolean {
  const token = useRef<object>({})

  useEffect(() => {
    if (!open) return
    const mine = token.current
    openSurfaces.push(mine)
    return () => {
      const i = openSurfaces.lastIndexOf(mine)
      if (i >= 0) openSurfaces.splice(i, 1)
    }
  }, [open])

  return useCallback(
    () => openSurfaces.length > 0 && openSurfaces[openSurfaces.length - 1] === token.current,
    [],
  )
}

/**
 * Escape and a click outside close the thing. One mechanism for every floating
 * surface in the app.
 *
 * ── WHY THIS IS A SHARED HOOK AND NOT TWO COPIES ────────────────────────────
 *
 * Before this, nothing in `web/src` handled a key at all — no Escape, no
 * outside-click, no focus return, anywhere. The bell panel had been openable
 * and closable only by pressing the bell again, which on a phone means the
 * panel covers what you wanted and the only way out is a target behind it.
 *
 * Two surfaces need it today (the bell and Picker) and there will be more. Two
 * hand-rolled copies is how one of them ends up listening on `click` instead of
 * `pointerdown` and closing after activating whatever was underneath.
 *
 * ── POINTERDOWN, NOT CLICK ──────────────────────────────────────────────────
 *
 * A press that begins outside should close on the way DOWN. On `click` the
 * surface is still open while the button under the pointer is being pressed,
 * so the press lands on the page as well as dismissing — which on a filter
 * popover over a table means opening somebody's record by accident.
 *
 * ── IT DOES NOT TRAP FOCUS, DELIBERATELY ────────────────────────────────────
 *
 * A trap belongs to a modal dialog, which stops the rest of the page. These are
 * menus: tabbing out of one is a legitimate way to leave, and the caller closes
 * on Tab if it wants that. The bell panel's `role="dialog"` was the same
 * category error in ARIA — it promised a modal to a screen reader and delivered
 * a dropdown — and it is gone for the same reason.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onDismiss: (viaKeyboard: boolean) => void,
) {
  // Held in a ref so an inline arrow function at the call site does not tear
  // down and re-register two document listeners on every render.
  const latest = useRef(onDismiss)
  latest.current = onDismiss

  const isTopmost = useSurfaceLayer(open)

  useEffect(() => {
    if (!open) return

    function onPointerDown(e: PointerEvent) {
      if (!ref.current?.contains(e.target as Node)) latest.current(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Only ours. Two open surfaces would otherwise both close on one press,
      // and the outer one is rarely what the person meant. `contains` alone
      // cannot separate a picker from the dialog AROUND it — both contain the
      // focused input — so the stack decides and this is the narrower test.
      if (!isTopmost()) return
      if (!ref.current?.contains(document.activeElement)) return
      e.stopPropagation()
      latest.current(true)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [ref, open, isTopmost])
}
